"use client";

import PageWrapper from "@/app/_components/PageWrapper";
import { Box, Card, Grid, Skeleton, Stack } from "@mui/material";

const mosaicGap = "6px";

function ImagesSkeleton() {
  return (
    <Stack spacing={1}>
      {/* mobile mosaic — matches ProductImagesView */}
      <Box sx={{ display: { xs: "block", md: "none" }, containerType: "inline-size" }}>
        <Box sx={{ display: "flex", gap: mosaicGap, alignItems: "flex-start" }}>
          <Skeleton
            variant="rounded"
            animation="wave"
            sx={{
              width: "calc(2 * (100cqi - 12px) / 2.5 + 6px)",
              aspectRatio: "1 / 1",
              height: "auto",
              flexShrink: 0,
              borderRadius: 3,
            }}
          />
          <Box
            sx={{
              width: "calc((100cqi - 12px) / 2.5)",
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              gap: mosaicGap,
            }}
          >
            <Skeleton variant="rounded" animation="wave" sx={{ aspectRatio: "1 / 1", height: "auto", width: "100%", borderRadius: 3 }} />
            <Skeleton variant="rounded" animation="wave" sx={{ aspectRatio: "1 / 1", height: "auto", width: "100%", borderRadius: 3 }} />
          </Box>
        </Box>
      </Box>

      {/* md+ main + thumbs */}
      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <Skeleton variant="rounded" animation="wave" sx={{ width: "100%", aspectRatio: "1 / 1", borderRadius: 3 }} />
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" animation="wave" sx={{ width: 64, height: 64, borderRadius: 2, flexShrink: 0 }} />
          ))}
        </Stack>
      </Box>
    </Stack>
  );
}

function TitleSkeleton() {
  return (
    <Stack spacing={{ xs: 2, md: 2 }}>
      <Stack spacing={0.75}>
        <Skeleton variant="text" animation="wave" width="90%" sx={{ fontSize: "1.5rem" }} />
        <Skeleton variant="text" animation="wave" width="55%" sx={{ fontSize: "1.5rem" }} />
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Skeleton variant="rectangular" animation="wave" height={1} sx={{ flexGrow: 1 }} />
          <Skeleton variant="text" animation="wave" width={100} height={18} />
        </Stack>
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Skeleton variant="text" animation="wave" width={110} height={18} />
        <Skeleton variant="text" animation="wave" width={80} height={18} />
        <Skeleton variant="text" animation="wave" width={70} height={18} />
      </Stack>
      {/* mobile specs strip */}
      <Stack direction="row" spacing={1} sx={{ display: { xs: "flex", md: "none" }, overflow: "hidden" }}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" animation="wave" height={56} sx={{ minWidth: 120, borderRadius: 2, flexShrink: 0 }} />
        ))}
      </Stack>
      {/* variant pickers */}
      <Stack spacing={1.25}>
        <Skeleton variant="text" animation="wave" width={72} height={20} />
        <Stack direction="row" spacing={1}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" animation="wave" width={44} height={44} sx={{ borderRadius: 2 }} />
          ))}
        </Stack>
      </Stack>
      <Skeleton variant="rounded" animation="wave" height={48} sx={{ borderRadius: 2 }} />
    </Stack>
  );
}

function VariantsSkeleton() {
  return (
    <Card
      elevation={0}
      sx={{
        boxShadow: "0 8px 28px rgba(33, 33, 33, 0.06)",
        backgroundColor: "#fff",
        borderRadius: 3,
        width: "100%",
        p: { xs: 1.5, sm: 2 },
        border: "1px solid",
        borderColor: "grey.100",
        height: "fit-content",
      }}
    >
      <Stack spacing={1.5}>
        <Skeleton variant="text" animation="wave" width="40%" height={18} />
        <Skeleton variant="text" animation="wave" width="55%" sx={{ fontSize: "1.75rem" }} />
        <Skeleton variant="rounded" animation="wave" height={40} sx={{ borderRadius: 2 }} />
        <Skeleton variant="rounded" animation="wave" height={36} sx={{ borderRadius: 2 }} />
        <Skeleton variant="rounded" animation="wave" height={48} sx={{ borderRadius: 2, display: { xs: "none", md: "block" } }} />
        <Stack spacing={0}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Stack key={i} direction="row" alignItems="center" spacing={1} sx={{ py: 1, px: 0.5, borderTop: "1px solid", borderColor: "grey.100" }}>
              <Skeleton variant="circular" animation="wave" width={18} height={18} />
              <Skeleton variant="text" animation="wave" width="70%" height={18} />
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Card>
  );
}

function TrustStripSkeleton() {
  return (
    <Box
      sx={{
        p: { xs: 1.25, sm: 1.5, md: 2 },
        display: "grid",
        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        gap: { xs: 1, sm: 1.5, md: 2 },
        borderRadius: 3,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "grey.100",
      }}
    >
      {Array.from({ length: 3 }).map((_, i) => (
        <Stack
          key={i}
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 0.75, sm: 1.25 }}
          alignItems="center"
          justifyContent={{ xs: "flex-start", sm: "center" }}
        >
          <Skeleton variant="rounded" animation="wave" sx={{ width: { xs: 36, sm: 44 }, height: { xs: 32, sm: 40 }, flexShrink: 0 }} />
          <Stack spacing={0.25} alignItems={{ xs: "center", sm: "flex-start" }} sx={{ width: "100%", minWidth: 0 }}>
            <Skeleton variant="text" animation="wave" width="70%" height={18} />
            <Skeleton variant="text" animation="wave" width="50%" height={14} sx={{ display: { xs: "none", sm: "block" } }} />
          </Stack>
        </Stack>
      ))}
    </Box>
  );
}

function MidSectionSkeleton() {
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1.5} sx={{ borderBottom: "1px solid", borderColor: "grey.100", pb: 1 }}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" animation="wave" width={72} height={32} sx={{ borderRadius: 2, display: i === 2 ? { xs: "none", md: "block" } : "block" }} />
        ))}
      </Stack>
      <Stack spacing={1}>
        <Skeleton variant="text" animation="wave" width="95%" />
        <Skeleton variant="text" animation="wave" width="88%" />
        <Skeleton variant="text" animation="wave" width="92%" />
        <Skeleton variant="text" animation="wave" width="70%" />
        <Skeleton variant="rounded" animation="wave" height={160} sx={{ borderRadius: 2, mt: 1 }} />
      </Stack>
    </Stack>
  );
}

function RelatedSkeleton() {
  return (
    <Grid item xs={12} component="aside">
      <Skeleton variant="text" animation="wave" width={160} sx={{ fontSize: { xs: "0.98rem", md: "1.4rem" }, mb: { xs: 1.5, md: 2 }, mt: { xs: 1, md: 2 } }} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            sm: "repeat(3, minmax(0, 1fr))",
            md: "repeat(4, minmax(0, 1fr))",
            lg: "repeat(5, minmax(0, 1fr))",
          },
          gap: { xs: 1, md: 1.5 },
        }}
      >
        {Array.from({ length: 5 }).map((_, i) => (
          <Stack
            key={i}
            spacing={1}
            sx={{
              display: {
                xs: i < 2 ? "flex" : "none",
                sm: i < 3 ? "flex" : "none",
                md: i < 4 ? "flex" : "none",
                lg: "flex",
              },
            }}
          >
            <Skeleton variant="rounded" animation="wave" sx={{ width: "100%", aspectRatio: "1 / 1.02", borderRadius: { xs: 2, sm: 4 } }} />
            <Skeleton variant="text" animation="wave" width="80%" height={18} />
            <Skeleton variant="text" animation="wave" width="50%" height={18} />
          </Stack>
        ))}
      </Box>
    </Grid>
  );
}

export default function Loading() {
  return (
    <main>
      <PageWrapper sx={{ mt: { xs: 1, md: 2 }, pb: { xs: 4, md: 6 } }}>
        <Grid container spacing={{ xs: 2, md: 3 }}>
          <Grid item xs={12}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, flexGrow: 1, overflow: "hidden" }}>
                <Skeleton variant="text" animation="wave" width={56} height={22} />
                <Skeleton variant="text" animation="wave" width={12} height={22} />
                <Skeleton variant="text" animation="wave" width={72} height={22} />
                <Skeleton variant="text" animation="wave" width={12} height={22} sx={{ display: { xs: "none", sm: "block" } }} />
                <Skeleton variant="text" animation="wave" width={100} height={22} sx={{ display: { xs: "none", sm: "block" } }} />
              </Stack>
              <Skeleton variant="circular" animation="wave" width={36} height={36} sx={{ flexShrink: 0 }} />
            </Stack>
          </Grid>

          <Grid item xs={12} component="article">
            <Box
              sx={{
                display: "grid",
                alignItems: "start",
                gap: { xs: 2, md: 2.5 },
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  md: "minmax(0, 5fr) minmax(0, 7fr)",
                  lg: "minmax(0, 1fr) minmax(0, 1.15fr) minmax(300px, 340px)",
                },
              }}
            >
              <Box sx={{ gridColumn: "1", minWidth: 0 }}>
                <ImagesSkeleton />
              </Box>

              <Box sx={{ gridColumn: { xs: "1", md: "2" }, minWidth: 0 }}>
                <TitleSkeleton />
              </Box>

              <Box
                sx={{
                  gridColumn: { xs: "1", md: "1 / -1", lg: "3" },
                  alignSelf: { xs: "start", lg: "stretch" },
                  minWidth: 0,
                }}
              >
                <VariantsSkeleton />
              </Box>

              <Box sx={{ gridColumn: "1 / -1", minWidth: 0 }}>
                <TrustStripSkeleton />
              </Box>

              <Box sx={{ gridColumn: "1 / -1", minWidth: 0 }}>
                <MidSectionSkeleton />
              </Box>
            </Box>
          </Grid>

          <RelatedSkeleton />
        </Grid>
      </PageWrapper>
    </main>
  );
}
